/**
 * Governed settings (mju-ai-workbench docs/plans/2026-09-project-model-end-to-end.md, R9): an
 * organisation policy covers every workbench and node setting, not only DLP. The keys are the
 * workbench's own (`shared/settings/declarations.json`, minus the `mjuWorkbench.` prefix), so the
 * console draws its editor from the declarations and validates values against them.
 *
 *   GET|PUT /admin/v1/settings-policy            the tenant's { settings, settingsMode }, governed with or without a published policy
 *   PUT     /projects/v1/:id/settings            owners: { settings?, settingsMode? } the project's overrides (the project's value wins)
 *   GET     /policy/v1[?projectId=]              carries the merged `settings` and `settingsMode`; a node pulls the tenant's without a project
 *
 * `settingsMode[key]`: "locked" means the organisation's value wins over every layer and the row
 * is read-only in the workbench; "default" means it sits above the product defaults and a person
 * may override it. A key with a value and no mode is "default".
 */
import { z } from "zod";
import { SETTINGS_DECLARATIONS } from "../settings/declarations.js";
export const settingsModeSchema = z.enum(["locked", "default"]);
const settingKeySchema = z.string().min(1).max(200).regex(/^[A-Za-z0-9_.-]+$/);
/** A block of governed settings: values by key, and how each one governs. */
export const settingsPolicySchema = z.object({
    settings: z.record(settingKeySchema, z.unknown()).default({}),
    settingsMode: z.record(settingKeySchema, settingsModeSchema).default({}),
});
const _projectSettingsMatch = true;
void _projectSettingsMatch;
/** Every declared setting, by key without the prefix. */
export const settingsDeclarations = SETTINGS_DECLARATIONS;
/** The group a key belongs to, the way the workbench's Settings view groups them: the first segment. */
export function settingsGroup(key) {
    return key.split(".")[0] ?? key;
}
/** Keys that are a node's own, governed by the tenant policy a node pulls without a project. */
export const isNodeSetting = (key) => key.startsWith("node.");
/** What is wrong with a settings block against the declarations, as sentences; empty when fine. */
export function settingsProblems(p, declarations = settingsDeclarations) {
    const out = [];
    for (const [key, value] of Object.entries(p.settings)) {
        const d = declarations[key];
        if (!d) {
            out.push(`${key}: not a setting the workbench declares`);
            continue;
        }
        const problem = valueProblem(d, value);
        if (problem)
            out.push(`${key}: ${problem}`);
    }
    for (const key of Object.keys(p.settingsMode))
        if (!declarations[key])
            out.push(`${key}: not a setting the workbench declares`);
    return out;
}
/** Whether a value fits a declaration: its type, enum, bounds and pattern. */
export function valueProblem(d, value) {
    switch (d.type) {
        case "string":
            if (typeof value !== "string")
                return "expects text";
            if (d.pattern && !new RegExp(d.pattern).test(value))
                return `does not match ${d.pattern}`;
            break;
        case "boolean":
            if (typeof value !== "boolean")
                return "expects on or off";
            break;
        case "number":
            if (typeof value !== "number" || Number.isNaN(value))
                return "expects a number";
            if (d.minimum !== undefined && value < d.minimum)
                return `is below ${d.minimum}`;
            if (d.maximum !== undefined && value > d.maximum)
                return `is above ${d.maximum}`;
            break;
        case "array":
            if (!Array.isArray(value))
                return "expects a list";
            break;
        case "object":
            if (typeof value !== "object" || value === null || Array.isArray(value))
                return "expects an object";
            break;
    }
    if (d.enum && !d.enum.includes(value))
        return `is not one of ${d.enum.map(String).join(", ")}`;
    return undefined;
}
/** Layer one settings block over another: the later wins, key by key. */
export function mergeSettingsPolicies(...layers) {
    const settings = {};
    const settingsMode = {};
    for (const l of layers) {
        if (!l)
            continue;
        Object.assign(settings, l.settings);
        Object.assign(settingsMode, l.settingsMode);
    }
    return { settings, settingsMode };
}
/** A short, stable digest of a settings block, for the policy's revision string. */
export function settingsDigest(p) {
    const text = JSON.stringify([Object.entries(p.settings).sort(), Object.entries(p.settingsMode).sort()]);
    let h = 5381;
    for (let i = 0; i < text.length; i++)
        h = ((h << 5) + h + text.charCodeAt(i)) >>> 0;
    return h.toString(36);
}
//# sourceMappingURL=settingsPolicy.js.map